import {AttrsSchema, Component} from 'panel';
import ResizeObserver from 'resize-observer-polyfill';
import {VNode} from 'snabbdom/vnode';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {ChartLegendTheme} from 'common/components/chart-legend/constants';
import {DisplayAnalysisType} from 'common/charts/visualization/line-chart/enums';
import {getCardTableChartLegendHeight} from 'common/components/chart-legend/utils';
import {LINE_CHART_PROP_KEYS, LineChartAttrs, LineChartProps} from 'common/charts/visualization/line-chart';
import {Section} from 'common/components/items-menu/types';
import {XAxisOptions} from 'common/charts/visualization/line-chart/types';
import {TimeUnit} from 'common/widgets/date-range-picker/models/types';

import template from './index.jade';
import css from './index.styl?inline';

export enum ChartDisplayContext {
  Primary = `primary`,
  DashboardCard = `dashboard-card`,
}

interface ChartAttrs {
  'display-analysis-type': DisplayAnalysisType;
  'display-context': ChartDisplayContext;
  'has-no-data': boolean;
  'incomplete-from-date': boolean;
  'incomplete-to-date': boolean;
  'is-compact': boolean;
  'legend-sections': Section[];
  'legend-theme': ChartLegendTheme;
  'show-annotations': boolean;
  'show-popover': boolean;
  timezone: string;
  'time-unit': TimeUnit;
  'utc-offset': number;
  'x-axis-options': XAxisOptions;
}

interface LegendLineChartState {
  lineChartProps: Partial<LineChartProps>;
  legendHeight: number; // px
}
export class LegendLineChart extends Component<LegendLineChartState, ChartAttrs> {
  static get attrsSchema(): AttrsSchema<ChartAttrs> {
    return {
      'display-analysis-type': {
        type: `string`,
        enum: Object.values(DisplayAnalysisType),
        default: DisplayAnalysisType.Linear,
      },
      'display-context': {
        type: `string`,
        enum: Object.values(ChartDisplayContext),
        default: ChartDisplayContext.Primary,
      },
      'has-no-data': `boolean`,
      'incomplete-from-date': `boolean`,
      'incomplete-to-date': `boolean`,
      'is-compact': `boolean`,
      'legend-sections': {type: `json`},
      'legend-theme': {type: `string`, enum: Object.values(ChartLegendTheme), default: ChartLegendTheme.Primary},
      'show-annotations': {
        type: `boolean`,
        description: `shows global annotations on mp-line-chart`,
      },
      'show-popover': {
        type: `boolean`,
        description: `show action popover on mp-line-chart`,
      },
      timezone: `string`,
      'time-unit': {
        type: `string`,
        default: null,
        enum: [TimeUnit.Minute, TimeUnit.Hour, TimeUnit.Day, TimeUnit.Month, TimeUnit.Week, TimeUnit.Quarter],
        description: `unit of line-chart that will be used for x axis bucketing`,
      },
      'utc-offset': `number`,
      'x-axis-options': {
        type: `json`,
        description: `x-axis overrides for highcharts of type XAxisOptions`,
      },
    };
  }

  private legendResizeObserver: ResizeObserver;

  constructor() {
    super();

    for (const key of LINE_CHART_PROP_KEYS) {
      Object.defineProperty(this, key, {
        get: () => this.state.lineChartProps[key],
        set: (value: any) => {
          this.update({
            lineChartProps: {
              ...this.state.lineChartProps,
              [key]: value,
            },
          });
        },
      });
    }
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      defaultState: {
        lineChartProps: {},
        legendHeight: 0,
      },
      helpers: {
        ChartLegendTheme,
        ChartDisplayContext,
        passThrough: (ev) => {
          ev.stopPropagation();
          this.dispatchEvent(new CustomEvent(ev.type, ev));
        },
        getChartLegendHeight: (): string => {
          if (this.attr(`legend-theme`) === ChartLegendTheme.CardTable) {
            return `${getCardTableChartLegendHeight(this.attr(`legend-sections`))}px`;
          }
          return `100%`;
        },
        getLineChartProps: () => this.state.lineChartProps,
        getLineChartAttrs: (): Partial<LineChartAttrs> => ({
          'chart-width': null,
          'display-analysis-type': this.attr(`display-analysis-type`),
          'incomplete-from-date': this.attr(`incomplete-from-date`),
          'incomplete-to-date': this.attr(`incomplete-to-date`),
          'show-annotations': this.attr(`show-annotations`),
          'show-popover': this.attr(`show-popover`),
          timezone: this.attr(`timezone`),
          'utc-offset': this.attr(`utc-offset`),
          'x-axis-options': this.attr(`x-axis-options`),
          'zoom-type': `x`,
          ...(this.attr(`time-unit`) ? {'time-unit': this.attr(`time-unit`)} : {}),
          ...this.getChartDisplayContextAttributes(),
        }),
        hasLegendSection: () => {
          return this.attr(`legend-sections`) && this.attr(`legend-sections`).length > 0;
        },
        getLegendSection: () =>
          this.attr(`display-context`) === ChartDisplayContext.DashboardCard
            ? this.attr(`legend-sections`).map((itemsObj) => ({
                items: itemsObj.items.filter((item) => item.isSelected),
              }))
            : this.attr(`legend-sections`),

        handleLegendInsert: (vnode: VNode) => {
          this.legendResizeObserver.observe(vnode.elm as Element);
        },
        handleLegendRemove: (vnode: VNode, removeCallback) => {
          this.legendResizeObserver.unobserve(vnode.elm as Element);
          removeCallback();
        },
      },
    };
  }

  connectedCallback() {
    this.legendResizeObserver = new ResizeObserver((entries) => {
      const {height} = entries[0].contentRect;
      this.update({legendHeight: height});
    });

    super.connectedCallback();
  }

  disconnectedCallback() {
    this.legendResizeObserver.disconnect();
    super.disconnectedCallback();
  }

  private getChartDisplayContextAttributes(): Partial<LineChartAttrs> {
    switch (this.attr(`display-context`)) {
      case ChartDisplayContext.DashboardCard:
        return {
          'chart-margin-right': 1,
          'chart-margin-left': 0,
          'y-axis-label-alignment': `left`,
          'y-axis-label-horizontal-offset': 2,
          'y-axis-label-vertical-offset': -2,
        };
      case ChartDisplayContext.Primary:
      default:
        return {};
    }
  }
}

export const LegendLineChartEl = defineAndMakeJsxFactory(`mp-legend-line-chart`, LegendLineChart);
